import { useState, useEffect } from 'react';

const STORAGE_KEY = 'milestone-map-goals';
const CHECKIN_STORAGE_KEY = 'milestone-map-checkins';

export function useGoals() {
  const [goals, setGoals] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [checkins, setCheckins] = useState(() => {
    const saved = localStorage.getItem(CHECKIN_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(goals));
  }, [goals]);

  useEffect(() => {
    localStorage.setItem(CHECKIN_STORAGE_KEY, JSON.stringify(checkins));
  }, [checkins]);

  const addGoal = (goal) => {
    const newGoal = {
      ...goal,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      milestones: goal.milestones.map((m, index) => ({
        ...m,
        id: `${Date.now()}-${index}`,
        completed: false
      }))
    };
    setGoals(prev => [...prev, newGoal]);
    return newGoal;
  };

  const updateGoal = (goalId, updates) => {
    setGoals(prev => prev.map(goal =>
      goal.id === goalId ? { ...goal, ...updates } : goal
    ));
  };

  const deleteGoal = (goalId) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
  };

  const toggleMilestone = (goalId, milestoneId) => {
    setGoals(prev => prev.map(goal => {
      if (goal.id !== goalId) return goal;
      return {
        ...goal,
        milestones: goal.milestones.map(m =>
          m.id === milestoneId ? { ...m, completed: !m.completed } : m
        )
      };
    }));
  };

  const addCheckin = (checkin) => {
    const newCheckin = {
      ...checkin,
      id: Date.now().toString(),
      date: new Date().toISOString()
    };
    setCheckins(prev => [...prev, newCheckin]);
    return newCheckin;
  };

  const getGoalProgress = (goal) => {
    if (!goal.milestones || goal.milestones.length === 0) return 0;
    const completed = goal.milestones.filter(m => m.completed).length;
    return Math.round((completed / goal.milestones.length) * 100);
  };

  const getOverallStats = () => {
    const totalGoals = goals.length;
    const completedGoals = goals.filter(g => getGoalProgress(g) === 100).length;
    const totalMilestones = goals.reduce((sum, g) => sum + (g.milestones?.length || 0), 0);
    const completedMilestones = goals.reduce(
      (sum, g) => sum + (g.milestones?.filter(m => m.completed).length || 0),
      0
    );
    const overallProgress = totalMilestones > 0
      ? Math.round((completedMilestones / totalMilestones) * 100)
      : 0;

    return {
      totalGoals,
      completedGoals,
      totalMilestones,
      completedMilestones,
      overallProgress
    };
  };

  const getTimelineEvents = () => {
    const events = [];

    goals.forEach(goal => {
      events.push({
        type: 'goal_created',
        date: goal.createdAt,
        title: `创建目标: ${goal.title}`,
        description: goal.description,
        goalId: goal.id
      });

      goal.milestones?.forEach(milestone => {
        if (milestone.completed) {
          events.push({
            type: 'milestone_completed',
            date: milestone.completedAt || goal.createdAt,
            title: `完成里程碑: ${milestone.title}`,
            description: `目标: ${goal.title}`,
            goalId: goal.id,
            milestoneId: milestone.id
          });
        }
      });
    });

    checkins.forEach(checkin => {
      events.push({
        type: 'checkin',
        date: checkin.date,
        title: '每周签到',
        description: checkin.reflection?.slice(0, 50) + (checkin.reflection?.length > 50 ? '...' : ''),
        checkinId: checkin.id
      });
    });

    return events.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);
  };

  return {
    goals,
    checkins,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleMilestone,
    addCheckin,
    getGoalProgress,
    getOverallStats,
    getTimelineEvents
  };
}

export const goalTemplates = [
  {
    title: '学习新技能',
    category: '个人成长',
    description: '掌握一门新技能，提升自我价值',
    milestones: [
      { title: '选择学习资源', targetDate: '' },
      { title: '完成基础知识学习', targetDate: '' },
      { title: '进行实践练习', targetDate: '' },
      { title: '完成一个实际项目', targetDate: '' }
    ]
  },
  {
    title: '健康生活',
    category: '健康',
    description: '建立健康的生活习惯',
    milestones: [
      { title: '制定运动计划', targetDate: '' },
      { title: '坚持一个月运动', targetDate: '' },
      { title: '调整饮食结构', targetDate: '' },
      { title: '达成目标体重/体能', targetDate: '' }
    ]
  },
  {
    title: '职业发展',
    category: '职业',
    description: '在职业道路上迈进一步',
    milestones: [
      { title: '明确职业目标', targetDate: '' },
      { title: '提升专业技能', targetDate: '' },
      { title: '拓展人脉网络', targetDate: '' },
      { title: '获得晋升/新机会', targetDate: '' }
    ]
  },
  {
    title: '财务自由',
    category: '财务',
    description: '建立稳固的财务基础',
    milestones: [
      { title: '评估当前财务状况', targetDate: '' },
      { title: '制定储蓄计划', targetDate: '' },
      { title: '建立应急基金', targetDate: '' },
      { title: '开始投资理财', targetDate: '' }
    ]
  }
];

export const motivationalQuotes = [
  { text: '千里之行，始于足下。', author: '老子' },
  { text: '不积跬步，无以至千里。', author: '荀子' },
  { text: '只要功夫深，铁杵磨成针。', author: '中国谚语' },
  { text: '成功不是终点，失败也不是末日，重要的是继续前行的勇气。', author: '温斯顿·丘吉尔' },
  { text: '把每一天当作生命的最后一天来过，你就会轻松自在。', author: '马可·奥勒留' }
];

export const weeklyQuestions = [
  '本周你在目标上取得了什么进展？',
  '遇到了什么挑战？你是如何应对的？',
  '下周你打算完成什么具体任务？',
  '有什么需要调整的计划吗？',
  '本周最让你感到骄傲的成就是什么？'
];
